import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import * as React from "react";
import { useState } from "react";
import { copyJsonObject, sortOnCaseSensitiveTexts } from "../../util/commonUtils";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import StarsSelectInput from "../fundamentals/StarsSelectInput";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import SuccessDialog from "../fundamentals/SuccessDialog";
import {
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_SAVE_YOUR_CHANGES_MESSAGE,
  FEATURE_ACCESS_CHANGES_SAVED_MESSAGE,
  FEATURE_ACCESS_CONFIRMATION_TITLE,
} from "../../util/messageConstants";
import { getAllAccessLinks, getFeaturesAndChildItems } from "../../api/commonConfig/goFeatureAccess";
import { getGOpsJobCodesList } from "../../api/commonConfig/jobCode";
import { getPresignedUrl, getStationList, uploadFileToS3 } from "../../api/system";
import PageLoading from "../PageLoading";
import {
  allAccessRequirements,
  blankFeatureConfigItem,
  buildFACPayload,
  featureAccessInitialErrorState,
  getS3FileInfix,
  getUploadMetadata,
} from "../../util/featureAccess/featureAccessUtils";
import {
  ALL_JOB_CODES,
  ALL_STATIONS,
  FAC_CONFIG_TYPES,
  FAC_EVENT_TYPE,
  FAC_FUNCTIONALITY,
  PATH_REQUIRES_SLASH_ERROR,
  REQUIRED_FIELD as REQUIRED_FIELD_ERROR,
} from "../../util/constants";

const useStyles = makeStyles((theme) => ({
  pageTitle: {
    fontSize: "32px",
    marginTop: "10px",
    marginLeft: "75px",
    textAlign: "center",
    color: theme.palette.primary.swaBlue,
  },
  actionButton: {
    width: "100px",
    margin: "5px",
  },
  functionalitySelectButtonGroup: {
    [theme.breakpoints.down("md")]: {
      margin: "auto",
    },
    display: "flex",
    direction: "column",
    justifyContent: "center",
    alignItems: "center",
    marginLeft: "100px",
    width: "100%",
    flexWrap: "wrap",
    color: theme.palette.secondary.swaWhite,
  },
  functionalitySelectButton: {
    [theme.breakpoints.down("md")]: {
      height: "36px",
      minWidth: "95vw",
      margin: "auto",
      marginTop: "5px",
      marginBottom: "5px",
    },
    height: "4em",
    width: "350px",
  },
  selectInputs: {
    [theme.breakpoints.down("md")]: {
      width: "24em",
      marginBottom: ".5em",
      maxWidth: 250,
    },
    maxWidth: 300,
    width: "21vw",
    textAlign: "left",
  },
  featureSectionContainer: {
    marginTop: "1rem",
    marginBottom: "1rem",
  },
  textInput: {
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
    width: "95%",
  },
  featureOrChildKeyInput: {
    [theme.breakpoints.down("md")]: {
      width: "100%",
      maxWidth: 300,
    },
    width: "95%",
  },
  accessLinkFeatureLabel: {
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
    width: "95%",
    maxWidth: 300,
  },
  smallChip: {
    marginRight: "5px",
    marginBottom: "5px",
  },
  requiredFieldError: {
    marginBottom: ".5em",
  },
}));

const FeatureAccessControlDialog = (props) => {
  const classes = useStyles();
  const [allStationsList, setAllStationsList] = useState([]);
  const [allJobCodesList, setAllJobCodesList] = useState([]);

  const [mainFeatures, setMainFeatures] = useState([]);
  const [childItems, setChildItems] = useState([]);
  const [accessLinks, setAccessLinks] = useState(null);

  const [currentConfigItem, setCurrentConfigItem] = useState(null);

  const [isFunctionalitySelected, setIsFunctionalitySelected] = useState(false);
  const [FACFunctionality, setFACFunctionality] = useState("");
  const [isChanged, setIsChanged] = useState(false);
  const [featureAccessErrors, setFeatureAccessErrors] = useState(copyJsonObject(featureAccessInitialErrorState));

  const [selectedFeature, setSelectedFeature] = useState("");
  const [selectedChildItem, setSelectedChildItem] = useState("");
  const [selectedAccessLink, setSelectedAccessLink] = useState("");
  const [stationForJobCodes, setStationForJobCodes] = useState("");

  const [isPageLoading, setIsPageLoading] = useState(false);

  const [isStationListLoading, setIsStationListLoading] = useState(false);
  const [isJobCodeListLoading, setIsJobCodeListLoading] = useState(false);
  const [isFeaturesLoading, setIsFeaturesLoading] = useState(false);
  const [isAccessLinkListLoading, setIsAccessLinkListLoading] = useState(false);

  const [isSubmissionLoading, setIsSubmissionLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState("");

  const [currentOverriddenEid, setCurrentOverriddenEid] = useState("");
  const [currentReport, setCurrentReport] = useState("");

  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

  // Load Edit Feature/Child Item
  if (
    FACFunctionality === FAC_FUNCTIONALITY.EDIT_FTR_OR_CI &&
    !isFeaturesLoading &&
    !isStationListLoading &&
    !isJobCodeListLoading &&
    isPageLoading
  ) {
    setIsPageLoading(false);
  }

  // Load Create New Feature
  if (
    FACFunctionality === FAC_FUNCTIONALITY.CREATE_FTR &&
    !isStationListLoading &&
    !isJobCodeListLoading &&
    !isAccessLinkListLoading &&
    isPageLoading
  ) {
    setIsPageLoading(false);
  }

  // Load Create New Child Item
  if (
    FACFunctionality === FAC_FUNCTIONALITY.CREATE_CI &&
    !isFeaturesLoading &&
    !isStationListLoading &&
    !isJobCodeListLoading &&
    isPageLoading
  ) {
    setIsPageLoading(false);
  }

  // Load Edit Access Links
  if (
    FACFunctionality === FAC_FUNCTIONALITY.EDIT_ACS_LNKS &&
    !isFeaturesLoading &&
    !isAccessLinkListLoading &&
    isPageLoading
  ) {
    setIsPageLoading(false);
  }

  const formatAllStationsList = () => {
    let formattedStations = [];
    allStationsList.forEach((station) => {
      let stationObject = {};
      stationObject[station] = {
        jobCodes: allJobCodesList,
      };
      formattedStations.push(stationObject);
    });
    return formattedStations;
  };

  const formatStationsForFeatureOrChildItemForClient = (stations) => {
    if (stations === ALL_STATIONS) {
      return formatAllStationsList();
    }

    let formattedStationList = [];

    stations.forEach((station) => {
      let stationObject = {};
      const stationKey = Object.keys(station)[0];
      const stationJobCodes = Object.values(station)[0]["jobCodes"];
      if (stationJobCodes === ALL_JOB_CODES) {
        stationObject[stationKey] = {
          jobCodes: allJobCodesList,
        };
      } else {
        stationObject[stationKey] = {
          jobCodes: stationJobCodes,
        };
      }
      formattedStationList.push(stationObject);
    });

    return formattedStationList;
  };

  const formatStationsForDDB = () => {
    const copiedConfigItem = getCopiedConfigItem();
    let stations = copiedConfigItem.stations;
    if (stations === ALL_STATIONS) {
      return ALL_STATIONS;
    }

    let isMissingJobCodes = false;
    for (let i = 0; i < stations.length; i++) {
      const station = Object.keys(stations[i])[0];
      const jobCodes = stations[i][station]["jobCodes"];
      if (jobCodes.length === allJobCodesList.length) {
        stations[i][station]["jobCodes"] = ALL_JOB_CODES;
      } else {
        isMissingJobCodes = true;
      }
    }

    if (!isMissingJobCodes && stations.length === allStationsList.length) {
      stations = ALL_STATIONS;
    }

    return stations;
  };

  const getCopiedConfigItem = () => {
    return copyJsonObject(currentConfigItem);
  };

  const loadMainFeaturesAndChildItems = () => {
    setIsFeaturesLoading(true);
    getFeaturesAndChildItems()
      .then((res) => res.json())
      .then((result) => {
        let features = {};
        let featureChildItems = {};
        result.forEach((item) => {
          if ("feature" in item) {
            features[item.feature.key] = item.feature;
          }

          if ("childItem" in item) {
            if (item.childItem.parentFeatureKey in featureChildItems) {
              featureChildItems[item.childItem.parentFeatureKey].push(item.childItem);
            } else {
              featureChildItems[item.childItem.parentFeatureKey] = [item.childItem];
            }
          }
        });
        setMainFeatures(features);
        setChildItems(featureChildItems);
        setIsFeaturesLoading(false);
      });
  };

  const loadStationList = () => {
    setIsStationListLoading(true);
    getStationList()
      .then((res) => res.json())
      .then((result) => {
        const stationResponse = result.stations.stns;
        const stationList = Object.keys(stationResponse).sort();
        setAllStationsList(stationList);
        setIsStationListLoading(false);
      });
  };

  const loadGOpsJobCodeList = () => {
    setIsJobCodeListLoading(true);
    getGOpsJobCodesList().then((result) => {
      setAllJobCodesList(result);
      setIsJobCodeListLoading(false);
    });
  };

  const loadAccessLinks = () => {
    let links = {};
    setIsAccessLinkListLoading(true);
    getAllAccessLinks()
      .then((res) => res.json())
      .then((result) => {
        result.forEach((item) => {
          links[item.accessLevel] = item.links;
        });
        setAccessLinks(links);
        setIsAccessLinkListLoading(false);
      });
  };

  const getMainFeaturesList = () => {
    return Object.keys(mainFeatures);
  };

  const getFeatureChildItemList = (featureKey) => {
    let childItemList = [];
    if (featureKey in childItems) {
      childItems[featureKey].forEach((item) => childItemList.push(item.key));
    }
    if (FACFunctionality === FAC_FUNCTIONALITY.EDIT_FTR_OR_CI && childItemList.length > 0) {
      childItemList.unshift("Main Feature");
    }
    return childItemList;
  };

  const getAccessLinksList = () => {
    return Object.keys(accessLinks);
  };

  const handleLabelChange = (event) => {
    const copiedConfigItem = getCopiedConfigItem();
    copiedConfigItem.label = event.target.value;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handlePathChange = (event) => {
    const copiedConfigItem = getCopiedConfigItem();
    const trimmedInput = event.target.value.trim();
    const formattedInput = trimmedInput.replace(/\s+/g, "");
    copiedConfigItem.path = formattedInput;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handleAdminPathChange = (event) => {
    const copiedConfigItem = getCopiedConfigItem();
    const trimmedInput = event.target.value.trim();
    const formattedInput = trimmedInput.replace(/\s+/g, "");
    copiedConfigItem.adminPath = formattedInput;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handleDescriptionChange = (event) => {
    const copiedConfigItem = getCopiedConfigItem();
    copiedConfigItem.description = event.target.value;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handleIsActiveToggle = () => {
    const copiedConfigItem = getCopiedConfigItem();
    copiedConfigItem.isActive = !copiedConfigItem.isActive;
    if (copiedConfigItem.isActive) {
      copiedConfigItem.stations = formatAllStationsList();
    } else {
      copiedConfigItem.stations = [];
    }
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handleIsHomeButtonToggle = () => {
    const copiedConfigItem = getCopiedConfigItem();
    copiedConfigItem.isHomeButton = !copiedConfigItem.isHomeButton;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handleIsActiveOnMobileToggle = () => {
    const copiedConfigItem = getCopiedConfigItem();
    copiedConfigItem.isActiveOnMobile = !copiedConfigItem.isActiveOnMobile;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handleIsHamburgerOptionToggle = () => {
    const copiedConfigItem = getCopiedConfigItem();
    copiedConfigItem.isHamburgerOption = !copiedConfigItem.isHamburgerOption;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handleStationsChange = (event) => {
    const copiedConfigItem = getCopiedConfigItem();
    const inputStations = event.target.value;

    let formattedStations = [];
    inputStations.forEach((station) => {
      let formattedStation = {};
      formattedStation[station] = { jobCodes: allJobCodesList };
      formattedStations.push(formattedStation);
    });
    // }

    copiedConfigItem.stations = formattedStations;
    setStationForJobCodes("");
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handleJobCodesChange = (event) => {
    const copiedConfigItem = getCopiedConfigItem();
    let stations;
    const selectedJobCodes = event.target.value;

    stations = copiedConfigItem.stations;

    for (let i = 0; i < stations.length; i++) {
      if (Object.keys(stations[i]).toString() === stationForJobCodes) {
        copiedConfigItem.stations[i][stationForJobCodes].jobCodes = selectedJobCodes;
        break;
      }
    }
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const displayFeatureChildItemSelect = () => {
    if (selectedChildItem === "") {
      return "Main Feature";
    }
    return selectedChildItem;
  };

  const handleFeatureChange = (event) => {
    const feature = event.target.value;
    setSelectedFeature(feature);
    mainFeatures[feature].stations = formatStationsForFeatureOrChildItemForClient(mainFeatures[feature].stations);
    setCurrentConfigItem(copyJsonObject(mainFeatures[feature]));
    setStationForJobCodes("");
    setSelectedChildItem("");
    setIsChanged(false);
  };

  const handleFeatureChildItemChange = (event, selectedFeature) => {
    const childItemKey = event.target.value;
    setStationForJobCodes("");
    if (childItemKey === "Main Feature") {
      setSelectedChildItem("");
      setCurrentConfigItem(mainFeatures[selectedFeature]);
    } else {
      setSelectedChildItem(childItemKey);
      let currentChildItem = copyJsonObject(
        childItems[selectedFeature].find((childItem) => childItem.key === childItemKey)
      );
      currentChildItem.stations = formatStationsForFeatureOrChildItemForClient(currentChildItem.stations);
      setCurrentConfigItem(currentChildItem);
    }
    setIsChanged(false);
  };

  const getSelectedStations = () => {
    let configStations;
    let stationList = [];
    configStations = currentConfigItem.stations;

    if (configStations === ALL_STATIONS || configStations.length === allStationsList.length) {
      stationList = allStationsList;
    } else {
      configStations.forEach((station) => {
        stationList.push(Object.keys(station).toString());
      });
    }
    return stationList;
  };

  const getJobCodesForStation = () => {
    let stations = currentConfigItem.stations;
    if (stations === ALL_STATIONS) {
      return allJobCodesList;
    }

    if (stations.length === 0 || stationForJobCodes === "") {
      return [];
    }
    const stationObj = stations.find((station) => Object.keys(station).toString() === stationForJobCodes);
    const jobCodes = stationObj[stationForJobCodes].jobCodes;
    return jobCodes;
  };

  const handleOverriddenEidChange = (event) => {
    const trimmedInput = event.target.value.trim();
    setCurrentOverriddenEid(trimmedInput);
  };

  const handleAccessRequirementsChange = (event) => {
    const copiedConfigItem = getCopiedConfigItem();
    copiedConfigItem.accessRequirements = event.target.value;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handleAddOverriddenEidEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleAddOverriddenEid(currentOverriddenEid);
    }
  };

  const handleAddOverriddenEid = (eid) => {
    const copiedConfigItem = getCopiedConfigItem();
    let overriddenEids = copiedConfigItem.overriddenEids;
    overriddenEids.push(eid);
    copiedConfigItem.overriddenEids = overriddenEids;
    setCurrentConfigItem(copiedConfigItem);
    setCurrentOverriddenEid("");
    setIsChanged(true);
  };

  const handleRemoveOverriddenEid = (eid) => {
    const copiedConfigItem = getCopiedConfigItem();
    let overriddenEids = copiedConfigItem.overriddenEids;
    const eidIdx = overriddenEids.findIndex((existingEid) => eid === existingEid);
    overriddenEids.splice(eidIdx, 1);
    copiedConfigItem.overriddenEids = overriddenEids;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const handleReportsChange = (event) => {
    setCurrentReport(event.target.value);
  };

  const handleAddReportEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleAddReport(currentReport);
    }
  };

  const handleAddReport = (report) => {
    const copiedConfigItem = getCopiedConfigItem();
    let reports = copiedConfigItem.reports;
    reports.push(report);
    copiedConfigItem.reports = reports;
    setCurrentConfigItem(copiedConfigItem);
    setCurrentReport("");
    setIsChanged(true);
  };

  const handleRemoveReport = (report) => {
    const copiedConfigItem = getCopiedConfigItem();
    let reports = copiedConfigItem.reports;
    const eidIdx = reports.findIndex((existingReport) => report === existingReport);
    reports.splice(eidIdx, 1);
    copiedConfigItem.reports = reports;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const validateRequiredFields = () => {
    setFeatureAccessErrors(copyJsonObject(featureAccessInitialErrorState));
    let errorState = copyJsonObject(featureAccessInitialErrorState);
    if (FACFunctionality !== FAC_FUNCTIONALITY.EDIT_ACS_LNKS) {
      if (currentConfigItem.key === "") {
        errorState.key = REQUIRED_FIELD_ERROR;
      }
      if (currentConfigItem.path === "") {
        errorState.path = REQUIRED_FIELD_ERROR;
      }
      if (currentConfigItem.path !== "" && !currentConfigItem.path.startsWith("/")) {
        errorState.path = PATH_REQUIRES_SLASH_ERROR;
      }
      if (!selectedChildItem && currentConfigItem.adminPath !== "" && !currentConfigItem.adminPath.startsWith("/")) {
        errorState.adminPath = PATH_REQUIRES_SLASH_ERROR;
      }
      if (currentConfigItem.label === "") {
        errorState.label = REQUIRED_FIELD_ERROR;
      }
    }
    setFeatureAccessErrors(errorState);
    return errorState.key === "" && errorState.path === "" && errorState.adminPath === "" && errorState.label === "";
  };

  const handleCancelDialogYesClick = () => {
    setIsWarningDialogOpen(false);
    props.setIsDialogOpen(false);
  };

  const handleCancelDialogNoClick = () => {
    setIsWarningDialogOpen(false);
  };

  const handleSuccessDialogOkClick = () => {
    setIsSuccessDialogOpen(false);
    if (submissionError === "") {
      props.setIsDialogOpen(false);
    } else {
      setSubmissionError("");
    }
  };

  const handleCancelButtonClick = () => {
    setIsWarningDialogOpen(true);
  };

  const handleSubmitButtonClick = () => {
    // Validate
    if (validateRequiredFields()) {
      setIsConfirmationDialogOpen(true);
    }
  };

  const handleConfirmationDialogNoClick = () => {
    setIsConfirmationDialogOpen(false);
  };

  const handleConfirmationDialogYesClick = () => {
    setIsSubmissionLoading(true);
    let stations = [];
    const metadata = getUploadMetadata(FACFunctionality, !!selectedChildItem, selectedAccessLink);
    if (metadata.configType !== FAC_CONFIG_TYPES.ACCESS_LINKS) {
      stations = formatStationsForDDB();
    }
    if (metadata.configType === FAC_CONFIG_TYPES.FEATURE && metadata.eventType === FAC_EVENT_TYPE.CREATE) {
      updateAccessLinksOnFeatureCreation(currentConfigItem.key);
    }
    const payload = buildFACPayload(currentConfigItem, metadata, stations);
    const fileInfix = getS3FileInfix(payload);

    getPresignedUrl("put_object", "data-passthrough", `FAC%2F${fileInfix}_fac.json`)
      .then((res) => res.json())
      .then((result) => {
        if (result.presignedUrl) {
          uploadFileToS3(result.presignedUrl, payload).then((response) => {
            if (response.status === 200) {
              console.log("Success!");
              setIsConfirmationDialogOpen(false);
              setIsSubmissionLoading(false);
              setIsSuccessDialogOpen(true);
            } else {
              console.error("Failure in S3 upload!");
              setSubmissionError("Failure in S3 upload!");
              setIsConfirmationDialogOpen(false);
              setIsSubmissionLoading(false);
              setIsSuccessDialogOpen(true);
            }
          });
        } else {
          console.error("Failure in getting S3 presigned url!");
          setSubmissionError("Failure in getting S3 presigned url!");
          setIsConfirmationDialogOpen(false);
          setIsSubmissionLoading(false);
          setIsSuccessDialogOpen(true);
        }
      });
  };

  const updateAccessLinksOnFeatureCreation = (newFeatureKey) => {
    for (const accessLevel in accessLinks) {
      let updatedAccessLinks = copyJsonObject(accessLinks[accessLevel]);
      updatedAccessLinks[newFeatureKey] = [];
      const metadata = getUploadMetadata(FAC_FUNCTIONALITY.EDIT_ACS_LNKS, false, accessLevel);
      const payload = buildFACPayload(updatedAccessLinks, metadata);
      const fileInfix = getS3FileInfix(payload);

      getPresignedUrl("put_object", "data-passthrough", `FAC%2F${fileInfix}_fac.json`)
        .then((res) => res.json())
        .then((result) => {
          if (result.presignedUrl) {
            uploadFileToS3(result.presignedUrl, payload).then((response) => {
              if (response.status === 200) {
                console.log(`Successful upload of ${accessLevel} access links`);
              } else {
                console.error(`Failed to upload ${accessLevel} access links`);
                setSubmissionError("Failure in S3 upload!");
              }
            });
          } else {
            console.error(`Failure in getting S3 presigned url during ${accessLevel} access links upload`);
            setSubmissionError("Failure in getting S3 presigned url!");
          }
        });
    }
  };

  const renderLabelDescriptionAndPathControls = () => {
    return (
      <Grid container justifyContent="center" spacing={2}>
        <Grid item xs={12}>
          <Grid container className={classes.featureSectionContainer}>
            <Grid item md={6} xs={12} mb={1}>
              <Grid item xs={12} mb={1}>
                <TextField
                  className={classes.textInput}
                  label="Label"
                  value={currentConfigItem.label}
                  variant="outlined"
                  onChange={(event) => handleLabelChange(event)}
                  error={!!featureAccessErrors.label}
                />
                {featureAccessErrors.label && (
                  <FormHelperText error className={classes.requiredFieldError}>
                    {featureAccessErrors.label}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  className={classes.textInput}
                  label="Description"
                  value={currentConfigItem.description}
                  variant="outlined"
                  multiline
                  maxRows={2}
                  onChange={(event) => handleDescriptionChange(event)}
                />
              </Grid>
            </Grid>
            <Grid item md={6} xs={12}>
              <Grid item xs={12} mb={1}>
                <TextField
                  className={classes.textInput}
                  label="Path"
                  value={currentConfigItem.path}
                  variant="outlined"
                  onChange={(event) => handlePathChange(event)}
                  error={!!featureAccessErrors.path}
                />

                {featureAccessErrors.path && (
                  <FormHelperText error className={classes.requiredFieldError}>
                    {featureAccessErrors.path}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12}>
                <Tooltip title={!!selectedChildItem ? "Child Items do not have an admin path" : ""} placement="bottom">
                  <TextField
                    className={classes.textInput}
                    label="Admin Path"
                    value={!!selectedChildItem ? "" : currentConfigItem.adminPath}
                    variant="outlined"
                    onChange={(event) => handleAdminPathChange(event)}
                    disabled={!!selectedChildItem}
                    error={!!featureAccessErrors.adminPath}
                  />
                </Tooltip>
                {featureAccessErrors.adminPath && (
                  <FormHelperText error className={classes.requiredFieldError}>
                    {featureAccessErrors.adminPath}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderToggleButtons = () => {
    return (
      <Grid container justifyContent="center">
        <Grid item xs={10}>
          <Grid container justifyContent="center" className={classes.featureSectionContainer}>
            <Grid item sm={3} xs={9}>
              <FormControlLabel
                control={<Switch checked={currentConfigItem.isActive} onChange={handleIsActiveToggle} />}
                label="Feature Active"
              />
            </Grid>
            <Grid item sm={3} xs={9}>
              <FormControlLabel
                control={<Switch checked={currentConfigItem.isHomeButton} onChange={handleIsHomeButtonToggle} />}
                label="Home Button"
              />
            </Grid>
            <Grid item sm={3} xs={9}>
              <FormControlLabel
                control={
                  <Switch checked={currentConfigItem.isActiveOnMobile} onChange={handleIsActiveOnMobileToggle} />
                }
                label="Active on Mobile"
              />
            </Grid>
            <Grid item sm={3} xs={9}>
              <Tooltip
                title={selectedChildItem ? "" : "Main Features must be present in the Hamburger Menu"}
                placement="bottom"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentConfigItem.isHamburgerOption}
                      onChange={handleIsHamburgerOptionToggle}
                      disabled={!selectedChildItem}
                    />
                  }
                  label="Hamburger Option"
                />
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderActiveStationAndJobCodeSelect = () => {
    return (
      <Grid container justifyContent="center">
        <Grid item xs={12}>
          <Grid container justifyContent="center" className={classes.featureSectionContainer}>
            <Grid item md={3} xs={12}>
              <StarsSelectInput
                className={classes.selectInputs}
                label="Active Stations"
                menuMessage="Select Active Stations"
                value={getSelectedStations()}
                allOptions={allStationsList}
                sortComparator={sortOnCaseSensitiveTexts}
                onChange={(event) => handleStationsChange(event)}
                errors=""
                multiple={true}
                disabled={!currentConfigItem.isActive}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <StarsSelectInput
                className={classes.selectInputs}
                label="Job Code Station"
                menuMessage="Select Station"
                value={stationForJobCodes}
                allOptions={getSelectedStations()}
                sortComparator={sortOnCaseSensitiveTexts}
                onChange={(event) => setStationForJobCodes(event.target.value)}
                errors=""
                multiple={false}
                disabled={getSelectedStations().length === 0}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <StarsSelectInput
                className={classes.selectInputs}
                label="Active Job Codes"
                menuMessage="Select Active Job Codes"
                value={getJobCodesForStation()}
                allOptions={allJobCodesList}
                sortComparator={sortOnCaseSensitiveTexts}
                onChange={(event) => handleJobCodesChange(event)}
                errors=""
                multiple={true}
                disabled={stationForJobCodes === ""}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderOverriddenEidListChips = () => {
    return currentConfigItem.overriddenEids.map((eid) => {
      return (
        <Chip key={eid} className={classes.smallChip} label={eid} onDelete={() => handleRemoveOverriddenEid(eid)} />
      );
    });
  };

  const renderReportChips = () => {
    return currentConfigItem.reports.map((report) => {
      return (
        <Chip key={report} className={classes.smallChip} label={report} onDelete={() => handleRemoveReport(report)} />
      );
    });
  };

  const renderOverriddenEidAndAccessRequirementAndReportsControls = () => {
    return (
      <Grid container justifyContent="center" className={classes.featureSectionContainer}>
        <Grid item md={3} xs={12}>
          <StarsSelectInput
            label="Access Requirements"
            menuMessage="Select Access Requirements"
            value={currentConfigItem.accessRequirements}
            allOptions={allAccessRequirements}
            sortComparator={sortOnCaseSensitiveTexts}
            onChange={handleAccessRequirementsChange}
            errors=""
            className={classes.selectInputs}
            multiple={true}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <Tooltip title="Add single Eid's to override and press 'Enter'" placement="bottom">
            <TextField
              className={classes.textInput}
              label="Override Eid's"
              value={currentOverriddenEid}
              variant="outlined"
              onChange={(event) => handleOverriddenEidChange(event)}
              onKeyUp={handleAddOverriddenEidEnterKeyPressed}
            />
          </Tooltip>
          <Grid container justifyContent="space-evenly">
            <Grid item xs={12} mt={1} mb={1}>
              {renderOverriddenEidListChips()}
            </Grid>
          </Grid>
        </Grid>

        <Grid item md={3} xs={12}>
          <Tooltip
            title={selectedChildItem ? "Child Items do not have Reports" : "Add report name and press 'Enter'"}
            placement="bottom"
          >
            <TextField
              className={classes.textInput}
              label="Reports"
              value={currentReport}
              variant="outlined"
              onChange={(event) => handleReportsChange(event)}
              onKeyUp={handleAddReportEnterKeyPressed}
              disabled={!!selectedChildItem}
            />
          </Tooltip>
          <Grid container justifyContent="space-evenly">
            <Grid item xs={12} mt={1} mb={1}>
              {selectedChildItem ? "" : renderReportChips()}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderFeatureSelection = () => {
    return (
      <DialogContent>
        <Grid container spacing={1} justifyContent="center" mt={1} mb={1}>
          <Grid item md={3} xs={12} justifyContent="center">
            <StarsSelectInput
              className={classes.selectInputs}
              label="Feature"
              menuMessage="Select a Feature"
              value={selectedFeature}
              allOptions={getMainFeaturesList()}
              sortComparator={sortOnCaseSensitiveTexts}
              onChange={(event) => handleFeatureChange(event)}
              errors=""
            />
          </Grid>
          {!!selectedFeature && getFeatureChildItemList(selectedFeature).length > 0 && (
            <Grid item md={3} xs={12}>
              <StarsSelectInput
                className={classes.selectInputs}
                label="Sub-Feature"
                menuMessage="Select a Sub-Feature"
                value={displayFeatureChildItemSelect()}
                allOptions={getFeatureChildItemList(selectedFeature)}
                sortComparator={sortOnCaseSensitiveTexts}
                onChange={(event) => handleFeatureChildItemChange(event, selectedFeature)}
                errors=""
              />
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} mt={2}>
          <Divider />
        </Grid>
        {!!currentConfigItem && renderFeatureControls()}
      </DialogContent>
    );
  };

  const renderFeatureControls = () => {
    return (
      <>
        {renderToggleButtons()}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {renderLabelDescriptionAndPathControls()}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {renderActiveStationAndJobCodeSelect()}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {renderOverriddenEidAndAccessRequirementAndReportsControls()}
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </>
    );
  };

  const handleCreateNewFeatureClicked = () => {
    setIsPageLoading(true);
    loadStationList();
    loadGOpsJobCodeList();
    loadAccessLinks();
    setCurrentConfigItem(copyJsonObject(blankFeatureConfigItem));
    setFACFunctionality(FAC_FUNCTIONALITY.CREATE_FTR);
    setIsFunctionalitySelected(true);
  };

  const handleNewFeatureKeyChange = (event) => {
    const copiedConfigItem = getCopiedConfigItem();
    const trimmedInput = event.target.value.trim();
    const formattedInput = trimmedInput.replace(/\s+/g, "");
    copiedConfigItem.key = formattedInput;
    setSelectedFeature(copiedConfigItem.key);
    setCurrentConfigItem(copiedConfigItem);
  };

  const renderCreateFeature = () => {
    return (
      <DialogContent>
        <Grid container spacing={1} justifyContent="center" mt={1} mb={1}>
          <Grid item md={3} xs={12} justifyContent="center">
            <Tooltip title={"Enter the key for your New Feature."} placement="bottom">
              <TextField
                className={classes.featureOrChildKeyInput}
                label="Feature Key"
                value={currentConfigItem.key}
                variant="outlined"
                onChange={(event) => handleNewFeatureKeyChange(event)}
                error={!!featureAccessErrors.key}
              />
            </Tooltip>
            {featureAccessErrors.key && (
              <FormHelperText error className={classes.requiredFieldError}>
                {featureAccessErrors.key}
              </FormHelperText>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>
        {selectedFeature && renderFeatureControls()}
      </DialogContent>
    );
  };

  const handleCreateNewChildItemClicked = () => {
    setIsPageLoading(true);
    loadMainFeaturesAndChildItems();
    loadStationList();
    loadGOpsJobCodeList();
    setCurrentConfigItem(copyJsonObject(blankFeatureConfigItem));
    setFACFunctionality(FAC_FUNCTIONALITY.CREATE_CI);
    setIsFunctionalitySelected(true);
  };

  const handleNewChildItemKeyChange = (event, selectedFeature) => {
    const copiedConfigItem = getCopiedConfigItem();
    const trimmedInput = event.target.value.trim();
    const formattedInput = trimmedInput.replace(/\s+/g, "");
    copiedConfigItem.key = formattedInput;
    copiedConfigItem["parentFeatureKey"] = selectedFeature;
    setSelectedChildItem(copiedConfigItem.key);
    setCurrentConfigItem(copiedConfigItem);
  };

  const renderCreateChildItem = () => {
    return (
      <DialogContent>
        <Alert severity="info">Access Links must be updated for Child Items to appear</Alert>
        <Grid container spacing={1} justifyContent="center" mt={1} mb={1}>
          <Grid item md={3} xs={12} justifyContent="center">
            <StarsSelectInput
              className={classes.selectInputs}
              label="Feature"
              menuMessage="Select a Feature"
              value={selectedFeature}
              allOptions={getMainFeaturesList()}
              sortComparator={sortOnCaseSensitiveTexts}
              onChange={(event) => setSelectedFeature(event.target.value)}
              errors=""
            />
          </Grid>
          {selectedFeature && (
            <Grid item md={3} xs={12} justifyContent="center">
              <Tooltip title={"Enter the key for your new Child Item."} placement="bottom">
                <TextField
                  className={classes.featureOrChildKeyInput}
                  label="Child Item Key"
                  value={currentConfigItem.key}
                  variant="outlined"
                  onChange={(event) => handleNewChildItemKeyChange(event, selectedFeature)}
                  error={!!featureAccessErrors.key}
                />
              </Tooltip>
              {featureAccessErrors.key && (
                <FormHelperText error className={classes.requiredFieldError}>
                  {featureAccessErrors.key}
                </FormHelperText>
              )}
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} mt={2}>
          <Divider />
        </Grid>
        {selectedChildItem && renderFeatureControls()}
      </DialogContent>
    );
  };

  const handleEditAccessLinksClicked = () => {
    setIsPageLoading(true);
    loadMainFeaturesAndChildItems();
    loadAccessLinks();
    setFACFunctionality(FAC_FUNCTIONALITY.EDIT_ACS_LNKS);
    setIsFunctionalitySelected(true);
  };

  const handleAccessLevelSelectionChange = (event) => {
    setSelectedAccessLink(event.target.value);
    setCurrentConfigItem(copyJsonObject(accessLinks[event.target.value]));
  };

  const handleAccessLinksSelectionChange = (event, feature) => {
    const copiedConfigItem = getCopiedConfigItem();
    const inputLinks = event.target.value;
    copiedConfigItem[feature] = inputLinks;
    setCurrentConfigItem(copiedConfigItem);
    setIsChanged(true);
  };

  const renderFeatureAccessLinks = () => {
    return (
      <>
        {!isAccessLinkListLoading && (
          <Grid container justifyContent="center" className={classes.featureSectionContainer}>
            {getMainFeaturesList()
              .filter((feature) => getFeatureChildItemList(feature).length > 0)
              .map((feature, idx) => {
                return (
                  <Grid key={feature + `${idx}`} item xs={12} md={12}>
                    <Grid container justifyContent="center" mb={2}>
                      <Grid key={feature} item md={3} xs={12} mb={1}>
                        <TextField
                          className={classes.accessLinkFeatureLabel}
                          label="Feature"
                          value={feature}
                          variant="outlined"
                          disabled
                        />
                      </Grid>
                      <Grid key={feature + "childItems"} item md={3} xs={12}>
                        <StarsSelectInput
                          className={classes.selectInputs}
                          label="Links"
                          menuMessage="Select Access Links"
                          value={feature in currentConfigItem ? currentConfigItem[feature] : []}
                          allOptions={getFeatureChildItemList(feature)}
                          sortComparator={sortOnCaseSensitiveTexts}
                          onChange={(event) => handleAccessLinksSelectionChange(event, feature)}
                          multiple={true}
                          disabled={getFeatureChildItemList(feature).length === 0}
                          errors=""
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
          </Grid>
        )}
      </>
    );
  };

  const renderAccessLinkSelection = () => {
    return (
      <DialogContent>
        <Grid container spacing={1} justifyContent="center" mt={1} mb={1}>
          <Grid item md={3} xs={12} justifyContent="center">
            <StarsSelectInput
              className={classes.selectInputs}
              label="Access Level"
              menuMessage="Select an Access Level"
              value={selectedAccessLink}
              allOptions={getAccessLinksList()}
              sortComparator={sortOnCaseSensitiveTexts}
              onChange={(event) => handleAccessLevelSelectionChange(event)}
              errors=""
            />
          </Grid>
        </Grid>
        <Grid item xs={12} mt={2}>
          <Divider />
        </Grid>
        {selectedAccessLink && renderFeatureAccessLinks()}
      </DialogContent>
    );
  };

  const renderActionDialogs = () => {
    return (
      <>
        {isWarningDialogOpen && (
          <ConfirmationDialog
            isDialogOpen={isWarningDialogOpen}
            message={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_MESSAGE}
            hasWarning={true}
            warningMessage={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE("changes")}
            handleNoButtonClicked={handleCancelDialogNoClick}
            handleYesButtonClicked={handleCancelDialogYesClick}
          />
        )}

        {isConfirmationDialogOpen && (
          <ConfirmationDialog
            isDialogOpen={isConfirmationDialogOpen}
            title={FEATURE_ACCESS_CONFIRMATION_TITLE}
            message={ARE_YOU_SURE_YOU_WANT_TO_SAVE_YOUR_CHANGES_MESSAGE}
            handleNoButtonClicked={handleConfirmationDialogNoClick}
            handleYesButtonClicked={handleConfirmationDialogYesClick}
            isYesLoading={isSubmissionLoading}
          />
        )}

        {isSuccessDialogOpen && (
          <SuccessDialog
            isSuccessDialogOpen={isSuccessDialogOpen}
            title={FEATURE_ACCESS_CONFIRMATION_TITLE}
            message={FEATURE_ACCESS_CHANGES_SAVED_MESSAGE}
            hasError={!!submissionError}
            error={submissionError}
            handleOKClick={handleSuccessDialogOkClick}
          />
        )}
      </>
    );
  };

  const routeFunctionalitySelection = () => {
    return (
      <>
        {FACFunctionality === FAC_FUNCTIONALITY.CREATE_FTR && renderCreateFeature()}
        {FACFunctionality === FAC_FUNCTIONALITY.CREATE_CI && renderCreateChildItem()}
        {FACFunctionality === FAC_FUNCTIONALITY.EDIT_FTR_OR_CI && renderFeatureSelection()}
        {FACFunctionality === FAC_FUNCTIONALITY.EDIT_ACS_LNKS && renderAccessLinkSelection()}
      </>
    );
  };

  const handleEditFeatureOrChildItemClick = () => {
    setIsPageLoading(true);
    loadMainFeaturesAndChildItems();
    loadStationList();
    loadGOpsJobCodeList();
    setFACFunctionality(FAC_FUNCTIONALITY.EDIT_FTR_OR_CI);
    setIsFunctionalitySelected(true);
  };

  const renderFunctionalitySelection = () => {
    return (
      <Grid container spacing={1} direction="column" justifyContent="center" alignItems="center" mt={1} mb={1}>
        <Grid item xs={3}>
          <Button
            className={classes.functionalitySelectButton}
            variant="contained"
            color="primary"
            onClick={handleCreateNewFeatureClicked}
          >
            Create New Feature
          </Button>
        </Grid>
        <Tooltip title={"Requires an existing parent feature."} placement="bottom">
          <Grid item xs={3}>
            <Button
              className={classes.functionalitySelectButton}
              variant="contained"
              color="primary"
              onClick={handleCreateNewChildItemClicked}
            >
              Create New Child Item
            </Button>
          </Grid>
        </Tooltip>
        <Grid item xs={3}>
          <Button
            className={classes.functionalitySelectButton}
            variant="contained"
            color="primary"
            onClick={handleEditFeatureOrChildItemClick}
          >
            Edit Feature/Child Item
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            className={classes.functionalitySelectButton}
            variant="contained"
            color="primary"
            onClick={handleEditAccessLinksClicked}
          >
            Edit Access Links
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Dialog
        fullWidth
        className=""
        maxWidth="lg"
        fullScreen={!props.isDesktopView}
        open={props.isDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        <DialogTitle>
          <Grid container justifyContent="space-between">
            <Grid item xs={4} sx={{ mt: 1.4 }}>
              Feature Access Control
            </Grid>
            <Grid item xs={3}>
              <DialogActions>
                <div>
                  <Button
                    className={classes.actionButton}
                    variant="outlined"
                    color="primary"
                    onClick={handleCancelButtonClick}
                  >
                    Cancel
                  </Button>
                </div>
              </DialogActions>
            </Grid>
          </Grid>
        </DialogTitle>
        {props.isDesktopView && (
          <Grid item xs={12}>
            <Divider variant="middle" />
          </Grid>
        )}

        {isPageLoading ? (
          <Grid container justifyContent="center" height="8.5em">
            <PageLoading />
          </Grid>
        ) : !isFunctionalitySelected ? (
          <>{renderFunctionalitySelection()}</>
        ) : (
          <>{routeFunctionalitySelection()}</>
        )}
        {isFunctionalitySelected && (
          <DialogActions>
            <div>
              <Button
                sx={{ width: "100px", marginRight: 3, marginBottom: 1 }}
                variant="contained"
                color="primary"
                onClick={handleSubmitButtonClick}
                disabled={!isChanged}
              >
                SUBMIT
              </Button>
            </div>
          </DialogActions>
        )}
      </Dialog>
      {renderActionDialogs()}
    </>
  );
};

FeatureAccessControlDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  setIsDialogOpen: PropTypes.func.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default FeatureAccessControlDialog;
